import { useState } from "react";
import { Download, Share2 } from "lucide-react";
import { inr, useMember } from "@/lib/api";
import { pdfAction } from "@/lib/pdf";
import { Field, Modal } from "@/components/common";

export default function TreatmentPlanForm({ patient, treatments, onClose }) {
  const member = useMember();
  const options = (treatments || []).filter((t) => t.status !== "cancelled");
  const [picked, setPicked] = useState(() => options.filter((t) => t.status === "planned" || t.status === "in_progress").map((t) => t.id));
  const [form, setForm] = useState({ discount: "", validity_days: "30", doctor_name: member?.role === "dentist" ? member.name || "" : "", notes: "" });
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const toggle = (id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  const subtotal = options.filter((t) => picked.includes(t.id)).reduce((s, t) => s + t.agreed_amount, 0);
  const total = subtotal - Number(form.discount || 0);
  const go = (mode) => pdfAction(mode, "post", "/pdf/treatment-plan", {
    patient_id: patient.id, treatment_ids: options.filter((t) => picked.includes(t.id)).map((t) => t.id),
    discount: Number(form.discount || 0), validity_days: Number(form.validity_days || 30), doctor_name: form.doctor_name, notes: form.notes,
  }, `Treatment estimate for ${patient.name}`);
  return (
    <Modal eyebrow="TREATMENT PLAN PDF" title={`Estimate for ${patient.name}`} onClose={onClose} onSubmit={() => go("download")} testid="treatment-plan-form">
      {options.length === 0 ? <p className="muted" data-testid="plan-no-treatments">Add the planned treatments first, then create the estimate.</p> : <>
        <p className="eyebrow">CHOOSE TREATMENTS</p>
        <div className="plan-list" data-testid="plan-treatment-list">
          {options.map((t) => (
            <label key={t.id} className={picked.includes(t.id) ? "plan-item on" : "plan-item"} data-testid={`plan-item-${t.id}`}>
              <input type="checkbox" checked={picked.includes(t.id)} onChange={() => toggle(t.id)} data-testid={`plan-check-${t.id}`} />
              <span><b>{t.treatment}</b><small>{[t.tooth && `Tooth ${t.tooth}`, t.status.replace("_", " ")].filter(Boolean).join(" · ")}</small></span>
              <em>{inr(t.agreed_amount)}</em>
            </label>
          ))}
        </div>
        <div className="form-two">
          <Field label="Discount (₹)" type="number" min="0" step="0.01" testid="plan-discount-input" value={form.discount} onChange={set("discount")} />
          <Field label="Valid for (days)" type="number" min="1" max="365" testid="plan-validity-input" value={form.validity_days} onChange={set("validity_days")} />
        </div>
        <Field label="Doctor name (for signature)" testid="plan-doctor-input" value={form.doctor_name} onChange={set("doctor_name")} placeholder="e.g. Dr. Mehta" />
        <Field label="Notes for the patient" textarea rows={2} testid="plan-notes-input" value={form.notes} onChange={set("notes")} />
        <div className="plan-total" data-testid="plan-total"><span>Total estimate</span><b>{inr(total)}</b></div>
        {total < 0 && <p className="plan-hint" data-testid="plan-discount-error">Discount can't be more than the subtotal of {inr(subtotal)}.</p>}
        <div className="share-row">
          <button className="primary" disabled={!picked.length || total < 0} data-testid="plan-download-button"><Download size={16} /> Download PDF</button>
          <button type="button" className="secondary" disabled={!picked.length || total < 0} onClick={() => go("share")} data-testid="plan-share-button"><Share2 size={16} /> Share on WhatsApp</button>
        </div>
      </>}
    </Modal>
  );
}
